<template>
  <el-drawer
    :visible.sync="localVisible"
    direction="rtl"
    :size="drawerSize"
    :with-header="false"
    :before-close="handleClose"
    append-to-body
    custom-class="bulk-message-drawer"
  >
    <div class="bmm-drawer-header">
      <h5 class="bmm-drawer-title">
        Enviar mensaje a {{ activeRecipients.length }} destinatario{{
          activeRecipients.length !== 1 ? "s" : ""
        }}
      </h5>
      <button class="bmm-drawer-close" @click="handleClose">
        <i class="el-icon-close"></i>
      </button>
    </div>
    <div class="bmm-body">
      <!-- Canal -->
      <div class="bmm-section">
        <label class="bmm-label">Canal de envío</label>
        <div class="bmm-channel-toggle">
          <button
            :class="['bmm-channel-btn', { active: channel === 'whatsapp' }]"
            @click="channel = 'whatsapp'"
          >
            <i class="el-icon-chat-round"></i> WhatsApp
          </button>
          <button
            :class="['bmm-channel-btn', { active: channel === 'email' }]"
            @click="channel = 'email'"
          >
            <i class="el-icon-message"></i> Email
          </button>
        </div>
      </div>

      <!-- WhatsApp no configurado — empty state -->
      <div v-if="channel === 'whatsapp' && !waReady" class="bmm-wa-empty">
        <div class="bmm-wa-empty-icon">
          <i class="el-icon-chat-round"></i>
        </div>
        <p class="bmm-wa-empty-title">WhatsApp no está configurado</p>
        <p class="bmm-wa-empty-sub">
          Para enviar mensajes por WhatsApp, primero conecta tu línea en
          Configuración → Integraciones.
        </p>
        <el-button
          type="primary"
          size="small"
          @click="$emit('go-to-whatsapp-config')"
        >
          <i class="el-icon-setting"></i> Configurar WhatsApp
        </el-button>
      </div>

      <!-- Formulario de envío (solo cuando el canal está disponible) -->
      <template
        v-if="channel === 'email' || (channel === 'whatsapp' && waReady)"
      >
        <!-- Selector de destinatarios (modo seleccionable) -->
        <div v-if="selectableMode" class="bmm-section">
          <label class="bmm-label">Destinatarios</label>

          <!-- Tags seleccionados (máx 3 visibles) -->
          <div v-if="selectedClients.length" class="bmm-tags">
            <el-tag
              v-for="c in selectedClients.slice(0, 3)"
              :key="c.id"
              size="small"
              closable
              type="info"
              @close="removeClient(c.id)"
              class="bmm-client-tag"
              >{{ c.name }}</el-tag
            >
            <el-tag
              v-if="selectedClients.length > 3"
              size="small"
              type="info"
              class="bmm-client-tag bmm-client-tag--count"
              >+{{ selectedClients.length - 3 }} más</el-tag
            >
          </div>

          <!-- Acciones -->
          <div class="bmm-select-actions">
            <el-button
              size="mini"
              plain
              @click="selectAll"
              :loading="clientsLoading"
              >Todos</el-button
            >
            <el-button size="mini" plain @click="clearClients"
              >Limpiar</el-button
            >
            <small class="bmm-selected-count"
              >{{ selectedClients.length }} seleccionados</small
            >
          </div>

          <!-- Buscador -->
          <el-autocomplete
            v-model="clientSearchQuery"
            :fetch-suggestions="searchClients"
            :placeholder="
              channel === 'whatsapp'
                ? 'Buscar por nombre o teléfono...'
                : 'Buscar por nombre o correo...'
            "
            size="small"
            style="width:100%;"
            :trigger-on-focus="false"
            @select="addClient"
            clearable
          >
            <template slot-scope="{ item }">
              <div class="bmm-ac-item">
                <span class="bmm-ac-item__name">{{ item.name }}</span>
                <small class="bmm-ac-item__contact">
                  {{
                    channel === "whatsapp" ? item.phone || "" : item.email || ""
                  }}
                </small>
              </div>
            </template>
          </el-autocomplete>
        </div>

        <!-- Destinatarios colapsables (modo fijo) -->
        <div v-else class="bmm-section">
          <div
            class="bmm-recipients-toggle"
            @click="showRecipients = !showRecipients"
          >
            <span class="bmm-label">
              Destinatarios ({{ activeRecipients.length }})
            </span>
            <i
              :class="
                showRecipients ? 'el-icon-arrow-up' : 'el-icon-arrow-down'
              "
            ></i>
          </div>
          <transition name="fade">
            <div v-if="showRecipients" class="bmm-recipients-list">
              <div
                v-for="r in activeRecipients"
                :key="r.id || r.name"
                class="bmm-recipient-item"
              >
                <div class="bmm-recipient-avatar">
                  {{ initials(r.name) }}
                </div>
                <div class="bmm-recipient-info">
                  <span class="bmm-recipient-name">{{ r.name }}</span>
                  <span class="bmm-recipient-contact">
                    {{
                      channel === "whatsapp"
                        ? r.phone || "Sin teléfono"
                        : r.email || "Sin email"
                    }}
                  </span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- Asunto (solo email) -->
        <div v-if="channel === 'email'" class="bmm-section">
          <label class="bmm-label">Asunto</label>
          <el-input
            v-model="subject"
            size="small"
            placeholder="Asunto del correo..."
            maxlength="150"
            show-word-limit
          />
        </div>

        <!-- Variables disponibles (solo email) -->
        <div v-if="channel === 'email'" class="bmm-section">
          <label class="bmm-label">Variables disponibles</label>
          <div class="bmm-vars">
            <button
              v-for="v in availableVariables"
              :key="v.key"
              class="bmm-var-chip"
              :title="v.label"
              @click="insertVariable(v.key)"
            >
              {{ v.key }}
            </button>
          </div>
        </div>

        <!-- Mensaje Email (rich text) -->
        <div v-if="channel === 'email'" class="bmm-section">
          <label class="bmm-label">Mensaje</label>
          <div class="bmm-editor-wrapper">
            <vue-editor
              ref="emailEditor"
              v-model="message"
              :editor-toolbar="emailToolbar"
              placeholder="Escribe tu mensaje aquí. Usa las variables de arriba para personalizarlo..."
            />
          </div>
        </div>

        <!-- Mensaje WhatsApp — selector de plantilla Meta -->
        <div v-if="channel === 'whatsapp'" class="bmm-section">
          <label class="bmm-label">Plantilla WhatsApp</label>
          <WhatsAppTemplatePicker
            :value="waTemplateData"
            :module-type="context"
            @input="onTemplateDataChange"
          />
        </div>

        <!-- Preview email (acordeón) -->
        <div
          v-if="channel === 'email' && hasMessage && activeRecipients.length"
          class="bmm-section"
        >
          <div class="bmm-preview-toggle" @click="showPreview = !showPreview">
            <label class="bmm-label bmm-label-clickable">
              Vista previa
              <span class="bmm-label-sub"
                >(primer destinatario: {{ firstRecipient.name }})</span
              >
            </label>
            <i
              :class="showPreview ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
              class="bmm-preview-arrow"
            ></i>
          </div>
          <transition name="fade">
            <div v-if="showPreview" class="bmm-preview">
              <div class="bmm-preview-html" v-html="resolvedPreview"></div>
            </div>
          </transition>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div
      class="bmm-footer"
      v-if="channel === 'email' || (channel === 'whatsapp' && waReady)"
    >
      <el-tooltip
        :disabled="canSend"
        :content="'Completa: ' + missingFields.join(', ')"
        placement="top"
        effect="dark"
      >
        <span style="display:block;">
          <el-button
            type="success"
            :disabled="!canSend"
            @click="confirmSend"
            class="bmm-send-btn"
            style="width:100%;"
          >
            <i class="el-icon-position"></i>
            Enviar a {{ activeRecipients.length || 0 }} contacto{{
              activeRecipients.length !== 1 ? "s" : ""
            }}
          </el-button>
        </span>
      </el-tooltip>
      <!-- Indicador de campos faltantes -->
      <transition name="fade">
        <p v-if="!canSend" class="bmm-missing-hint">
          <i class="el-icon-warning-outline"></i>
          Falta: {{ missingFields.join(" · ") }}
        </p>
      </transition>
    </div>
  </el-drawer>
</template>

<script>
import notificationService from "@/modules/dashboard/business/services/notificationService";
import businessService from "@/modules/dashboard/business/services/businessService";
import configService from "@/modules/dashboard/configurations/services/configService";
import { signingEventBus } from "@/services/signingEventBus";
import { VueEditor } from "vue2-editor";
import WhatsAppTemplatePicker from "@/modules/dashboard/components/WhatsAppTemplatePicker";

const QUOTE_VARIABLES = [
  { key: "{{nombre_cliente}}", label: "Nombre del cliente", field: "name" },
  {
    key: "{{numero_cotizacion}}",
    label: "Número de cotización",
    field: "quoteCode"
  },
  { key: "{{monto}}", label: "Monto", field: "quoteMonto" },
  { key: "{{dias_vencida}}", label: "Días vencida", field: "quoteDays" }
];

const INVOICE_VARIABLES = [
  { key: "{{nombre_cliente}}", label: "Nombre del cliente", field: "name" },
  {
    key: "{{numero_factura}}",
    label: "Número de factura",
    field: "invoiceCode"
  },
  { key: "{{monto}}", label: "Monto", field: "invoiceMonto" },
  { key: "{{dias_vencida}}", label: "Días vencida", field: "invoiceDays" }
];

const COMMON_VARIABLES = [
  {
    key: "{{unsubscribe_url}}",
    label: "Link cancelar suscripción",
    field: null
  }
];

// Templates pre-cargados por contexto (email usa HTML para respetar formato)
const DEFAULT_TEMPLATES = {
  quotes: {
    subject: "Seguimiento cotización {{numero_cotizacion}}",
    whatsapp:
      "Hola {{nombre_cliente}}, le escribimos respecto a la cotización {{numero_cotizacion}} por {{monto}} enviada hace {{dias_vencida}}. ¿Ha tenido oportunidad de revisarla? Quedamos atentos a sus comentarios.",
    email:
      '<p>Estimado/a <strong>{{nombre_cliente}}</strong>,</p><p>Le escribimos para dar seguimiento a la cotización <strong>{{numero_cotizacion}}</strong> por un valor de <strong>{{monto}}</strong>, enviada hace <strong>{{dias_vencida}}</strong>.</p><p>¿Ha tenido oportunidad de revisarla? Estamos disponibles para resolver cualquier consulta.</p><p>Saludos cordiales</p><p style="margin-top:30px;font-size:12px;color:#999;text-align:center;"><a href="{{unsubscribe_url}}" style="color:#999;text-decoration:underline;">Cancelar suscripción</a></p>'
  },
  invoices: {
    subject: "Recordatorio de pago — Factura {{numero_factura}}",
    whatsapp:
      "Hola {{nombre_cliente}}, le recordamos que la factura {{numero_factura}} por {{monto}} se encuentra pendiente de pago desde hace {{dias_vencida}}. ¿Podría indicarnos el estado del pago? Gracias.",
    email:
      '<p>Estimado/a <strong>{{nombre_cliente}}</strong>,</p><p>Le recordamos que la factura <strong>{{numero_factura}}</strong> por un valor de <strong>{{monto}}</strong> se encuentra pendiente de pago (<strong>{{dias_vencida}}</strong> de atraso).</p><p>Le agradecemos gestionar el pago a la brevedad. Si ya fue realizado, por favor haga caso omiso de este mensaje.</p><p>Saludos cordiales</p><p style="margin-top:30px;font-size:12px;color:#999;text-align:center;"><a href="{{unsubscribe_url}}" style="color:#999;text-decoration:underline;">Cancelar suscripción</a></p>'
  },
  clients: {
    subject: "Nos gustaría saber de usted",
    whatsapp:
      "Hola {{nombre_cliente}}, hace tiempo que no tenemos contacto. ¿Hay algo en lo que podamos ayudarle? Nos encantaría retomar la conversación.",
    email:
      '<p>Estimado/a <strong>{{nombre_cliente}}</strong>,</p><p>Ha pasado un tiempo desde nuestro último contacto y nos gustaría saber cómo se encuentra.</p><p>¿Hay algún proyecto o necesidad en la que podamos apoyarle? Estamos a su disposición.</p><p>Saludos cordiales</p><p style="margin-top:30px;font-size:12px;color:#999;text-align:center;"><a href="{{unsubscribe_url}}" style="color:#999;text-decoration:underline;">Cancelar suscripción</a></p>'
  }
};

export default {
  name: "BulkMessageModal",
  props: {
    visible: { type: Boolean, default: false },
    recipients: { type: Array, default: () => [] },
    context: { type: String, default: "quotes" },
    selectableMode: { type: Boolean, default: false },
    availableClients: { type: Array, default: () => [] },
    whatsappEnabled: { type: Boolean, default: false }
  },
  data() {
    return {
      channel: "whatsapp",
      subject: "",
      message: "",
      waTemplateData: null, // { templateId, templateName, variables, template }
      showRecipients: false,
      showPreview: false,
      bulkJobId: null,
      pollInterval: null,
      sendProgress: null,
      selectedClients: [], // array of { id, name, email, phone }
      clientSearchQuery: "",
      clientsLoading: false,
      whatsappReadyInternal: false, // chequeo propio del modal
      whatsappCheckDone: false, // evita parpadeo durante carga inicial
      emailToolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ color: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"]
      ]
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    // DEV: localStorage.setItem('wa_dev_mode', '1') para forzar UI completa
    waReady() {
      if (
        typeof localStorage !== "undefined" &&
        localStorage.getItem("wa_dev_mode") === "1"
      ) {
        return true;
      }
      // Si el padre ya lo confirma, no necesitamos chequeo propio
      if (this.whatsappEnabled) return true;
      // Chequeo propio (en caso de que el padre no lo pase actualizado)
      return this.whatsappReadyInternal;
    },
    drawerSize() {
      if (typeof window === "undefined") return "420px";
      return window.innerWidth < 768 ? "100%" : "420px";
    },
    availableVariables() {
      let vars;
      if (this.context === "quotes") vars = QUOTE_VARIABLES;
      else if (this.context === "invoices") vars = INVOICE_VARIABLES;
      else vars = QUOTE_VARIABLES.filter(v => v.field === "name");
      return [...vars, ...COMMON_VARIABLES];
    },
    activeRecipients() {
      if (this.selectableMode) {
        return this.selectedClients;
      }
      return this.recipients;
    },
    selectedClientIds() {
      return this.selectedClients.map(c => c.id);
    },
    firstRecipient() {
      return this.activeRecipients[0] || {};
    },
    hasMessage() {
      if (this.channel === "whatsapp") return !!this.waTemplateData?.templateId;
      return this.message.replace(/<[^>]*>/g, "").trim().length > 0;
    },
    hasRecipients() {
      return this.activeRecipients.length > 0;
    },
    hasSubject() {
      if (this.channel !== "email") return true;
      return this.subject.trim().length > 0;
    },
    canSend() {
      return this.hasRecipients && this.hasSubject && this.hasMessage;
    },
    missingFields() {
      const missing = [];
      if (!this.hasRecipients) missing.push("destinatario");
      if (!this.hasSubject) missing.push("asunto");
      if (this.channel === "whatsapp" && !this.waTemplateData?.templateId)
        missing.push("plantilla WhatsApp");
      else if (this.channel === "email" && !this.hasMessage)
        missing.push("mensaje");
      return missing;
    },
    resolvedPreview() {
      let text = this.message;
      const r = this.firstRecipient;
      const fmt = v =>
        "$" +
        new Intl.NumberFormat("es-EC", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(v || 0);
      text = text.replace(/\{\{nombre_cliente\}\}/g, r.name || "Cliente");
      text = text.replace(
        /\{\{numero_cotizacion\}\}/g,
        r.quoteCode || "COT-001"
      );
      text = text.replace(
        /\{\{numero_factura\}\}/g,
        r.invoiceCode || "FAC-001"
      );
      text = text.replace(
        /\{\{monto\}\}/g,
        fmt(r.quoteMonto || r.invoiceMonto)
      );
      text = text.replace(
        /\{\{dias_vencida\}\}/g,
        (r.quoteDays || r.invoiceDays || 0) + " días"
      );
      return text;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadDefaults();
        this.clientsToShow = [...this.availableClients];
        // Verificar config de WhatsApp cada vez que se abre el drawer
        this.checkWhatsAppStatus();
      }
    },
    channel(val) {
      // Al cambiar canal, actualizar mensaje con el template del nuevo canal
      this.loadMessageTemplate();
      // Si se cambia a WhatsApp, refrescar el estado por si la config se acaba de hacer
      if (val === "whatsapp") {
        this.checkWhatsAppStatus();
      }
      // Filtrar destinatarios seleccionados que no tienen el dato necesario
      // para el canal actual (los descarta silenciosamente)
      if (this.selectedClients.length) {
        const key = val === "whatsapp" ? "phone" : "email";
        this.selectedClients = this.selectedClients.filter(c => c[key]);
      }
    }
  },
  methods: {
    onTemplateDataChange(data) {
      this.waTemplateData = data;
    },

    async checkWhatsAppStatus() {
      // Si el padre ya pasó whatsappEnabled=true, no necesitamos chequear
      if (this.whatsappEnabled) {
        this.whatsappReadyInternal = true;
        this.whatsappCheckDone = true;
        return;
      }
      // Modo prueba salta el chequeo
      if (localStorage.getItem("wa_dev_mode") === "1") {
        this.whatsappReadyInternal = true;
        this.whatsappCheckDone = true;
        return;
      }
      try {
        const res = await configService.getWhatsAppConfig();
        const cfg = res.data?.data;
        this.whatsappReadyInternal = !!(
          cfg &&
          cfg.status === "active" &&
          cfg.is_active
        );
      } catch {
        this.whatsappReadyInternal = false;
      } finally {
        this.whatsappCheckDone = true;
      }
    },

    loadDefaults() {
      const tpl = DEFAULT_TEMPLATES[this.context] || DEFAULT_TEMPLATES.quotes;
      this.subject = tpl.subject || "";
      this.loadMessageTemplate();
      this.selectedClients = [];
      this.clientSearchQuery = "";
    },
    loadMessageTemplate() {
      const tpl = DEFAULT_TEMPLATES[this.context] || DEFAULT_TEMPLATES.quotes;
      this.message =
        this.channel === "email" ? tpl.email || "" : tpl.whatsapp || "";
    },
    insertVariable(varKey) {
      if (this.channel === "email") {
        const quill = this.$refs.emailEditor?.quill;
        if (quill) {
          const range = quill.getSelection() || {
            index: quill.getLength() - 1
          };
          quill.insertText(range.index, varKey, "user");
          quill.setSelection(range.index + varKey.length);
          quill.focus();
          return;
        }
      }
      // WhatsApp — textarea
      const textarea = this.$refs.messageInput?.$el?.querySelector("textarea");
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        this.message =
          this.message.substring(0, start) +
          varKey +
          this.message.substring(end);
        this.$nextTick(() => {
          textarea.selectionStart = start + varKey.length;
          textarea.selectionEnd = start + varKey.length;
          textarea.focus();
        });
      } else {
        this.message += varKey;
      }
    },
    async searchClients(query, cb) {
      try {
        const res = await businessService.getBusiness({
          params: { name: query || "" }
        });
        const raw = res.data?.clients || [];
        let results = raw.map(c => ({
          id: c.id,
          name: c.name || c.business_name || "",
          email: c.email || null,
          phone: c.mobile || c.phone || null,
          value: c.name || c.business_name || "" // required by el-autocomplete
        }));
        // En WhatsApp: ocultar contactos sin teléfono
        if (this.channel === "whatsapp") {
          results = results.filter(c => c.phone);
        } else {
          // En email: ocultar contactos sin email
          results = results.filter(c => c.email);
        }
        cb(results);
      } catch {
        cb([]);
      }
    },
    addClient(client) {
      if (!this.selectedClients.find(c => c.id === client.id)) {
        this.selectedClients = [...this.selectedClients, client];
      }
      this.clientSearchQuery = "";
    },
    removeClient(id) {
      this.selectedClients = this.selectedClients.filter(c => c.id !== id);
    },
    clearClients() {
      this.selectedClients = [];
    },
    async selectAll() {
      this.clientsLoading = true;
      try {
        let page = 1,
          lastPage = 1,
          all = [];
        do {
          const res = await businessService.getBusiness({
            params: { page, per_page: 100 }
          });
          const { clients, pagination } = res.data || {};
          if (Array.isArray(clients)) all = all.concat(clients);
          lastPage = pagination?.last_page ?? 1;
          page++;
        } while (page <= lastPage);

        let mapped = all.map(c => ({
          id: c.id,
          name: c.name || "",
          email: c.email || null,
          phone: c.mobile || c.phone || null
        }));
        // Filtrar según canal: solo contactos con el dato necesario
        if (this.channel === "whatsapp") {
          mapped = mapped.filter(c => c.phone);
        } else {
          mapped = mapped.filter(c => c.email);
        }
        this.selectedClients = mapped;
      } catch {
        /* ignore */
      } finally {
        this.clientsLoading = false;
      }
    },
    async confirmSend() {
      const count = this.activeRecipients.length;
      try {
        await this.$confirm(
          `Vas a enviar un mensaje a ${count} contacto${
            count !== 1 ? "s" : ""
          } por ${
            this.channel === "whatsapp" ? "WhatsApp" : "Email"
          }. ¿Confirmar?`,
          "Confirmar envío",
          {
            confirmButtonText: "Sí, enviar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );
        this.doSend();
      } catch {
        // Cancelado
      }
    },
    async doSend() {
      const count = this.activeRecipients.length;
      const channel = this.channel;
      const toastId = `bulk-${Date.now()}`;

      // Snapshot before closing
      const validRecipients = this.activeRecipients.filter(r => r.id);
      if (!validRecipients.length) {
        this.$message.error("No hay destinatarios válidos.");
        return;
      }

      // ── DEV MODE: simular envío sin tocar el backend ──
      if (
        channel === "whatsapp" &&
        localStorage.getItem("wa_dev_mode") === "1"
      ) {
        signingEventBus.$emit("email:start", {
          id: toastId,
          clientName: `${count} contacto${count !== 1 ? "s" : ""}`,
          documentNumber: "WhatsApp (prueba)"
        });
        this.$emit("update:visible", false);
        this.reset();
        await new Promise(r => setTimeout(r, 800));
        signingEventBus.$emit("email:done", {
          id: toastId,
          clientName: `✓ [Modo prueba] ${count} mensaje${
            count !== 1 ? "s" : ""
          } simulado${count !== 1 ? "s" : ""} con plantilla "${this
            .waTemplateData?.templateName || ""}"`,
          documentNumber: ""
        });
        this.$emit("sent", { channel, simulated: true });
        return;
      }

      const clientIds = validRecipients.map(r => r.id);

      // Build per-recipient variable map for backend substitution
      const recipientVariables = {};
      validRecipients.forEach(r => {
        recipientVariables[r.id] = {
          numero_cotizacion: r.quoteCode ?? null,
          numero_factura: r.invoiceCode ?? null,
          monto: r.quoteMonto ?? r.invoiceMonto ?? null,
          dias_vencida: r.quoteDays ?? r.invoiceDays ?? null
        };
      });

      const payload = {
        subject: channel === "email" ? this.subject : undefined,
        body: channel === "email" ? this.message : undefined,
        channel,
        client_ids: clientIds,
        recipient_variables:
          channel === "email" ? recipientVariables : undefined,
        whatsapp_template_id:
          channel === "whatsapp"
            ? this.waTemplateData?.templateId || undefined
            : undefined,
        whatsapp_variables:
          channel === "whatsapp"
            ? this.waTemplateData?.variables || []
            : undefined
      };

      // Close drawer immediately
      this.$emit("update:visible", false);
      this.reset();

      // Show toast with spinner (reuses the same toast as signing/email)
      signingEventBus.$emit("email:start", {
        id: toastId,
        clientName: `${count} contacto${count !== 1 ? "s" : ""}`,
        documentNumber: channel === "email" ? "Correo" : "WhatsApp"
      });

      try {
        // skipErrorNotification: el interceptor global no muestra toast,
        // el bus de signing se encarga de mostrar el error en el toast inferior
        const createRes = await notificationService.createNotification(
          payload,
          {
            skipErrorNotification: true
          }
        );
        if (!createRes?.data?.success) {
          throw new Error(
            createRes?.data?.message || "Error al crear notificación"
          );
        }

        const sendRes = await notificationService.sendNotification(
          createRes.data.data.id,
          { skipErrorNotification: true }
        );
        if (!sendRes?.data?.success) {
          throw new Error(sendRes?.data?.message || "Error al enviar");
        }

        if (channel === "email") {
          const isAsync = sendRes.data?.async === true;

          if (isAsync) {
            // Job queued in background — show neutral toast and rely on polling for final status
            signingEventBus.$emit("email:done", {
              id: toastId,
              clientName: `Procesando envío a ${count} contacto${
                count !== 1 ? "s" : ""
              }...`,
              documentNumber: ""
            });
          } else {
            const notification = sendRes.data?.data || {};
            const sent = notification.sent_count ?? 0;
            const total = notification.total_recipients ?? count;

            if (sent > 0) {
              const failed = total - sent;
              const subtitle =
                failed > 0
                  ? `${sent} enviado${
                      sent !== 1 ? "s" : ""
                    }, ${failed} fallido${failed !== 1 ? "s" : ""}`
                  : `${sent} de ${total} enviado${sent !== 1 ? "s" : ""}`;
              signingEventBus.$emit("email:done", {
                id: toastId,
                clientName: subtitle,
                documentNumber: ""
              });
            } else {
              const apiError = sendRes.data?.error || "";
              let msg =
                "0 correos enviados. Verifica Resend en Configuraciones.";
              if (/testing emails to your own email/i.test(apiError)) {
                msg =
                  "Modo prueba: Resend solo permite enviar a info@mydessk.com. Verifica tu dominio.";
              } else if (/domain is not verified/i.test(apiError)) {
                msg =
                  "Dominio no verificado en Resend. Ve a resend.com/domains.";
              } else if (/api key/i.test(apiError)) {
                msg =
                  "API Key de Resend inválida. Revisa Configuraciones → Servidor de correo.";
              }
              signingEventBus.$emit("email:error", {
                id: toastId,
                message: msg
              });
            }
          }

          if (sendRes.data?.bulk_job_id)
            this.startPolling(sendRes.data.bulk_job_id);
        } else {
          const wp = sendRes.data?.whatsapp || {};
          const sent = wp.sent ?? 0;
          const failed = wp.failed ?? 0;
          const apiError = sendRes.data?.error;

          if (sent === 0 && (failed > 0 || apiError)) {
            // Todos fallaron — mostrar el error real de WhatsApp/Meta
            signingEventBus.$emit("email:error", {
              id: toastId,
              message:
                apiError ||
                "No se pudo enviar ningún mensaje por WhatsApp. Revisa la plantilla y la configuración."
            });
          } else {
            const subtitle =
              failed > 0
                ? `${sent} enviado${sent !== 1 ? "s" : ""}, ${failed} fallido${
                    failed !== 1 ? "s" : ""
                  }`
                : `${sent} mensaje${sent !== 1 ? "s" : ""} vía WhatsApp`;
            signingEventBus.$emit("email:done", {
              id: toastId,
              clientName: subtitle,
              documentNumber: ""
            });
          }
        }

        this.$emit("sent", { channel });
      } catch (err) {
        // Extraer mensaje real del backend (no el genérico de axios)
        const backendMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Error inesperado al enviar.";
        signingEventBus.$emit("email:error", {
          id: toastId,
          message: backendMsg
        });
      }
    },

    startPolling(jobId) {
      let attempts = 0;
      const maxAttempts = 60; // ~2 min with 2s interval

      this.pollInterval = setInterval(async () => {
        attempts++;
        try {
          const res = await notificationService.getBulkJobStatus(jobId);
          const job = res?.data;
          if (!job) return;

          this.sendProgress = job;

          const done = [
            "completed",
            "completed_with_errors",
            "failed"
          ].includes(job.status);

          if (done || attempts >= maxAttempts) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;

            if (job.status === "completed") {
              this.$message.success(
                `${job.sent} email${job.sent !== 1 ? "s" : ""} enviado${
                  job.sent !== 1 ? "s" : ""
                } exitosamente.`
              );
            } else if (job.status === "completed_with_errors") {
              this.$message.warning(
                `${job.sent} enviados, ${job.failed} fallidos.`
              );
            } else if (job.status === "failed") {
              this.$message.error(
                "El envío masivo falló. Revisa los logs en el admin."
              );
            }

            this.$emit("sent", { channel: "email", jobId, progress: job });
            this.reset();
          }
        } catch (e) {
          // Silently continue polling on transient errors
        }
      }, 2000);
    },
    reset() {
      this.message = "";
      this.subject = "";
      this.channel = "whatsapp";
      this.waTemplateData = null;
      this.showRecipients = false;
      this.showPreview = false;
      this.selectedClients = [];
      this.clientSearchQuery = "";
      this.bulkJobId = null;
      this.sendProgress = null;
    },
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    handleClose() {
      this.$emit("update:visible", false);
    }
  },
  components: { VueEditor, WhatsAppTemplatePicker },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.bmm-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.bmm-drawer-title {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.bmm-drawer-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: $bg;
    color: $primary;
  }
}
.bmm-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}
.bmm-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bmm-label {
  font-size: 12px;
  font-weight: 700;
  color: $primary;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.bmm-label-sub {
  font-size: 11px;
  font-weight: 400;
  color: #909399;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 6px;
}

// Canal toggle
.bmm-channel-toggle {
  display: flex;
  gap: 8px;
}
.bmm-channel-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border: 1px solid $border;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  transition: all 0.15s;
  &:hover {
    border-color: $primary;
    color: $primary;
  }
  &.active {
    border-color: $primary;
    background: $primary;
    color: #fff;
  }
}
.bmm-wa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  flex: 1;
}
.bmm-wa-empty-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0fdf4;
  border: 2px solid #d1fae5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  i {
    font-size: 32px;
    color: #3ecfb4;
  }
}
.bmm-wa-empty-title {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 8px;
}
.bmm-wa-empty-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 20px;
  line-height: 1.5;
}

// Variables chips
.bmm-vars {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bmm-var-chip {
  background: #eff2ff;
  color: $primary;
  border: 1px solid #c7cff5;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  font-family: monospace;
  &:hover {
    background: $primary;
    color: #fff;
    border-color: $primary;
  }
}

// Preview acordeón
.bmm-preview-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid $border;
  background: $bg;
  transition: background 0.15s;
  &:hover {
    background: #eef0f7;
  }
}
.bmm-label-clickable {
  cursor: pointer;
  margin: 0;
}
.bmm-preview-arrow {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
}

// Preview
.bmm-preview {
  background: $bg;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 12px;
}
.bmm-preview-text {
  font-size: 13px;
  color: #303133;
  white-space: pre-wrap;
  margin: 0;
  line-height: 1.6;
}

// Recipients
.bmm-recipients-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  i {
    color: #909399;
    font-size: 12px;
  }
}
.bmm-recipients-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 4px 0;
}
.bmm-recipient-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  &:hover {
    background: $bg;
  }
}
.bmm-recipient-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: $primary;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.bmm-recipient-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.bmm-recipient-name {
  font-size: 12px;
  font-weight: 600;
  color: $primary;
}
.bmm-recipient-contact {
  font-size: 11px;
  color: #909399;
}

// Tags de clientes seleccionados
.bmm-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}
.bmm-client-tag {
  border-radius: 6px !important;
  background: #eff2ff !important;
  border-color: #c7cff5 !important;
  color: $primary !important;
  font-size: 12px !important;

  &--count {
    background: $primary !important;
    border-color: $primary !important;
    color: #fff !important;
    font-weight: 600 !important;
  }
}

// Autocomplete dropdown item
.bmm-ac-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1px 0;
  &__name {
    font-size: 13px;
    font-weight: 600;
    color: $primary;
    flex-shrink: 0;
  }
  &__contact {
    font-size: 11px;
    color: #909399;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
}

// Selector de destinatarios
.bmm-select-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.bmm-selected-count {
  font-size: 11px;
  color: #909399;
  margin-left: 4px;
}

.bmm-footer {
  padding: 16px 20px;
  border-top: 1px solid $border;
  flex-shrink: 0;
}
.bmm-missing-hint {
  margin: 8px 0 0;
  font-size: 11px;
  color: #f59e0b;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  i {
    font-size: 12px;
  }
}

.bmm-send-btn {
  width: 100%;
  border-radius: 10px;
  font-size: 14px;
  padding: 10px 0;
  background-color: #3ecfb4 !important;
  border-color: #3ecfb4 !important;
  color: #fff !important;

  &:hover,
  &:focus {
    background-color: #0e8c87 !important;
    border-color: #0e8c87 !important;
    color: #fff !important;
  }

  &.is-disabled,
  &[disabled] {
    opacity: 0.5;
    background-color: #3ecfb4 !important;
    border-color: #3ecfb4 !important;
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, max-height 0.25s;
  overflow: hidden;
  max-height: 300px;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>

<style lang="scss">
.bulk-message-drawer {
  display: flex;
  flex-direction: column;
}

// ── Quill editor overrides inside bulk drawer ──
.bulk-message-drawer .bmm-editor-wrapper {
  .ql-toolbar {
    border-radius: 10px 10px 0 0;
    border-color: #e4eaf6;
    background: #f8f9fc;
    padding: 6px 10px;

    .ql-formats {
      margin-right: 8px;
    }

    button,
    .ql-picker {
      color: #31355c;
      &:hover,
      &.ql-active {
        color: #3ecfb4;
      }
    }
    .ql-stroke {
      stroke: #31355c;
    }
    button:hover .ql-stroke,
    button.ql-active .ql-stroke {
      stroke: #3ecfb4;
    }
    .ql-fill {
      fill: #31355c;
    }
  }

  .ql-container {
    border-radius: 0 0 10px 10px;
    border-color: #e4eaf6;
    font-family: inherit;
    font-size: 14px;
  }

  .ql-editor {
    min-height: 160px;
    max-height: 340px;
    overflow-y: auto;
    color: #374151;
    line-height: 1.7;

    p {
      margin: 0 0 10px;
    }
    p:last-child {
      margin-bottom: 0;
    }

    &.ql-blank::before {
      color: #c0c4cc;
      font-style: normal;
      font-size: 13px;
    }
  }
}

// ── Preview HTML ──
.bulk-message-drawer .bmm-preview-html {
  font-size: 13px;
  color: #303133;
  line-height: 1.7;

  p {
    margin: 0 0 10px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong {
    font-weight: 600;
  }
  ul,
  ol {
    padding-left: 20px;
    margin: 0 0 10px;
  }
}
</style>
